import React, { useState } from 'react';
import { Shield } from 'lucide-react';

interface SurveyConsentScreenProps {
  onAccept: () => void;
}

const TERMS_CONTENT = `BLUEPILL - SURVEY PLATFORM
TERMS AND CONDITIONS & DATA USAGE CONSENT

Last Updated: May 18, 2026
Version: 2.1


1. AGREEMENT TO TERMS

By accessing and participating in surveys hosted on the BluePill survey platform ("Platform"), you agree to be bound by these Terms and Conditions and our Data Usage Policy. If you do not agree to these terms, please do not participate in any surveys.

2. DATA COLLECTION

When you participate in surveys on our Platform, we collect the following types of data:

2.1 RESPONSE DATA
• Text responses to open-ended and closed-ended questions
• Multiple choice, rating scale, and ranking selections
• Numeric and percentage allocation inputs
• MaxDiff preference selections

2.2 VOICE DATA
• Audio recordings of voice responses
• Transcriptions of audio content converted to text
• Voice input metadata (duration, quality metrics)

2.3 IMAGE DATA
• Photos uploaded in response to survey questions
• Product images for packaging evaluation questions
• Image hotspot click coordinates and selections
• Product identification data extracted from uploaded images

2.4 BEHAVIORAL & TECHNICAL DATA
• Response timestamps and completion times
• Input mode preferences (voice vs. text)
• Session duration and engagement metrics
• Quality flags (speeding, straightlining, attention)
• IP address and geographic location (country/region)
• Browser type, operating system, and device type
• Screen resolution and time zone
• Interaction patterns (mouse/keyboard activity, scrolling)
• External tracking IDs (if provided)
• Referrer URL and campaign parameters (if present)
• Duplicate detection across sessions

3. USE OF DATA FOR MACHINE LEARNING - INTERNAL USE ONLY

*** IMPORTANT NOTICE ***

BY PARTICIPATING IN THIS SURVEY, YOU ACKNOWLEDGE AND CONSENT THAT ALL DATA COLLECTED (INCLUDING TEXT RESPONSES, VOICE RECORDINGS, TRANSCRIPTIONS, AND IMAGES) MAY BE USED BY BLUEPILL TO TRAIN, IMPROVE, AND DEVELOP MACHINE LEARNING MODELS AND ARTIFICIAL INTELLIGENCE SYSTEMS FOR OUR OWN PRODUCTS AND SERVICES.

3.1 INTERNAL USE COMMITMENT
• Your data is used ONLY by BluePill for our own ML model development
• We DO NOT sell, rent, or license your raw survey data to third parties
• We DO NOT provide your individual responses to other companies
• BluePill creates and owns all surveys on this platform

3.2 MACHINE LEARNING PURPOSES
Your data may be used internally for:

- Training natural language processing (NLP) models to understand consumer sentiment, preferences, and behavior patterns
- Improving speech recognition and audio transcription accuracy
- Developing computer vision models for product recognition and image analysis
- Enhancing AI-powered follow-up question generation algorithms
- Building predictive models for market research and consumer insights
- Creating synthetic personas based on aggregated response patterns
- Improving survey quality detection and validation systems
- Training conversational AI for automated research interviews
- Developing and improving our AI audience simulation technology

3.3 DATA PROCESSING BY AI SERVICE PROVIDERS
While we do not sell your data, we use third-party AI services to process and analyze your responses. These providers act as our "data processors" and are contractually required to protect your data:

- OpenAI (GPT-4) - Audio transcription and text analysis
• Anthropic (Claude) - Text analysis, follow-up generation, image analysis
• Google (Gemini/Vertex AI) - Multimodal analysis

IMPORTANT: When you submit voice/text/images, these AI providers process your data to generate insights. While we don't "sell" your data, these providers may use the processing interactions to improve their own models per their terms of service. Review their policies:
• OpenAI: https://openai.com/policies/privacy-policy
• Anthropic: https://www.anthropic.com/privacy
• Google: https://cloud.google.com/terms/cloud-privacy-notice

3.4 HOW YOUR DATA IMPROVES OUR PRODUCTS
• Your responses help us build better AI personas for market research
• Your voice recordings improve our speech-to-text accuracy
• Your uploaded images train our product recognition systems
• Your feedback patterns enhance our survey design tools
• Your interaction patterns improve fraud detection algorithms
• Your engagement metrics help optimize survey UX/design

3.5 FUTURE DATA COLLECTION
We may collect additional data types in the future, including eye-tracking, facial expressions, advanced biometrics, or social media integrations. If we introduce significant new data collection, we will update these terms and obtain fresh consent where required by law.

4. DATA SHARING & USAGE

4.1 WHO HAS ACCESS TO YOUR DATA
BluePill is the survey creator and owner of all surveys on this platform. Your response data is used internally by BluePill for:
• Market research analysis and reporting
• ML model training and product development
• Quality assurance and fraud prevention

4.2 WHAT WE DO NOT DO
• We do NOT sell your individual responses to data brokers
• We do NOT use your data for targeted advertising to you
• We do NOT license your raw data to competing research platforms
• We do NOT share your data with third parties except as disclosed in these terms (AI processors, cloud storage)

5. DATA RETENTION & STORAGE

5.1 RETENTION PERIOD
• Survey response data is retained for the lifetime of our service for ML model training and product improvement
• Voice recordings are stored for up to 5 YEARS after which audio files are deleted (transcripts may be retained longer)
• Image data is stored on Amazon S3 (US regions)
• You may request deletion at any time (see Section 6)

5.2 STORAGE & SECURITY
• All data is encrypted in transit (TLS 1.3) and at rest (AES-256)
• Data is stored on Amazon Web Services (AWS) in United States regions
• Redis cache used for temporary session management
• PostgreSQL database for structured data storage
• Access to raw data is restricted to authorized BluePill personnel

5.3 BACKUPS
• Regular database backups are maintained for disaster recovery
• Deleted data is removed from backups after backup rotation cycle completes

6. DATA ANONYMIZATION & PRIVACY

6.1 ANONYMIZATION
• We do not collect personally identifiable information (PII) such as name, email, or phone number unless explicitly required by specific survey questions
• Each survey session is assigned a unique anonymous Customer ID
• Your responses are separated from direct identifiers

6.2 PII HANDLING
• If a survey question asks for PII (e.g., "What is your name?"), we store that response as part of your survey data
• We do NOT use PII for ML training or cross-survey analysis
• PII is used only for the research purpose of that specific survey
• ML models are trained on anonymized, non-PII data

6.3 LIMITATIONS
• While we anonymize data, you should not include sensitive personal information in your open-ended responses unless specifically asked
• Voice recordings contain vocal characteristics that could be considered biometric identifiers
• Uploaded photos should not contain faces or identifiable individuals

7. YOUR DATA RIGHTS

7.1 ACCESS
• Request a copy of your survey responses by contacting the survey creator with your Customer ID or external tracking ID

7.2 DELETION / RIGHT TO BE FORGOTTEN
• Request deletion of your data by emailing support@blue-pill.ai
• Include your Customer ID or external tracking ID
• We will delete your raw data within 30 days
• NOTE: ML models already trained on your data cannot "unlearn" patterns, but we will remove the source data

7.3 CORRECTION
• Request correction of inaccurate data within 30 days of survey completion

7.4 OPT-OUT LIMITATIONS
• Once you begin a survey, you consent to data use for that session
• You cannot retroactively opt-out of ML training after submission
• Future surveys: simply do not participate if you don't consent

7.5 DO NOT SELL (CCPA)
• California Residents: We do NOT sell your personal information to third parties. However, our use of AI service providers (OpenAI, Anthropic, Google) may constitute "sharing" under CCPA.
• To opt-out of sharing with AI processors, you cannot participate in surveys (as they are necessary for survey functionality)

To exercise these rights, contact:
• Email: support@blue-pill.ai
• Subject: "Data Rights Request - Survey Platform"
• Include: Customer ID, survey ID, and specific request

8. BIOMETRIC DATA NOTICE (VOICE RECORDINGS)

*** IMPORTANT FOR VOICE/AUDIO RESPONSES ***

8.1 BIOMETRIC IDENTIFIER DISCLOSURE
• Voice recordings are considered "biometric identifiers" under some state laws (Illinois, Texas, Washington, California)
• Your voiceprint (unique vocal characteristics) can identify you

8.2 SPECIFIC CONSENT REQUIRED
By submitting voice responses, you specifically consent to:
• Collection of your voice recordings
• Storage for up to 5 YEARS
• Use for speech recognition model training
• Processing by OpenAI (transcription service)
• Permanent deletion after retention period

8.3 PURPOSE LIMITATION
• Voice data is used ONLY for:
  - Transcribing your survey responses
  - Improving speech-to-text accuracy
  - Training our own voice analysis models
• NOT used for:
  - Voice authentication/identification
  - Sharing with third parties (except AI processors)
  - Marketing or advertising

8.4 BIOMETRIC DATA SECURITY
• Encrypted storage (AES-256)
• Access limited to authorized personnel
• Regular security audits

8.5 NO SALE OF BIOMETRIC DATA
• We will NEVER sell, lease, or trade your voice recordings
• Third-party processors (OpenAI) are bound by data processing agreements

9. INTELLECTUAL PROPERTY

9.1 YOUR CONTENT
• You retain ownership of your original responses
• By submitting responses, you grant BluePill a perpetual, worldwide, royalty-free, non-exclusive license to:
  - Use, reproduce, and modify your responses
  - Create derivative works (synthetic data, trained models)
  - Incorporate into our ML training datasets
  - Use for product development and improvement
• This license does NOT include the right to sell your raw data to third parties

9.2 PLATFORM CONTENT
• All survey questions, platform design, and AI-generated content remain the property of BluePill or survey creators

10. GEOGRAPHIC RESTRICTIONS

10.1 IP-BASED ACCESS CONTROL
• Surveys may be restricted to specific countries (e.g., US-only)
• We use third-party geolocation services to verify your location
• We collect your IP address for fraud prevention and location verification
• Access from restricted countries will be blocked
• VPN/proxy use to circumvent restrictions is prohibited

10.2 CURRENT RESTRICTIONS
• Many surveys are limited to United States participants only
• Some surveys may be limited to specific states

11. COMPENSATION

Some surveys may offer compensation (gift cards or other rewards). If offered:
• Compensation terms will be specified at the start of the survey
• Low-quality or fraudulent responses may void compensation
• Surveys may close early; not all participants are guaranteed rewards

12. PROHIBITED CONDUCT

You agree NOT to:
• Provide false, misleading, or fraudulent responses
• Use automated bots or scripts to complete surveys
• Attempt to participate in the same survey multiple times
• Upload illegal, offensive, or copyrighted content
• Upload images containing identifiable faces (without consent)
• Reverse engineer or scrape the platform
• Use VPNs to circumvent geographic restrictions
• Deliberately provide low-quality responses to receive compensation

13. QUALITY STANDARDS & FRAUD DETECTION

13.1 AUTOMATED QUALITY MONITORING
We use automated systems to detect:
• Response speeding (completing questions too quickly)
• Straightlining (selecting the same answer repeatedly)
• Gibberish or nonsensical text
• Duplicate participation (IP addresses, tracking IDs)
• VPN/proxy usage
• Bot-like behavior patterns
• Suspicious interaction patterns

13.2 CONSEQUENCES OF LOW QUALITY
• Session may be terminated without compensation
• Data may be excluded from analysis
• Future survey access may be restricted

13.3 ATTENTION CHECKS
• Surveys may include attention verification questions
• Failing multiple checks may result in early termination

14. DISCLAIMERS

14.1 AS-IS SERVICE
• The Platform is provided "AS IS" without warranties of any kind
• We do not guarantee uninterrupted service or error-free operation
• Technical issues may result in data loss or survey interruption

14.2 NO LIABILITY FOR TECHNICAL ISSUES
• BluePill is not liable for:
  - Server downtime or maintenance
  - Data transmission errors
  - Browser/device compatibility issues
  - Loss of survey progress due to internet disconnection

14.3 THIRD-PARTY SERVICES
• We are not responsible for the performance of third-party AI services (OpenAI, Anthropic, Google)

14.4 NOT PROFESSIONAL ADVICE
• Survey participation does not constitute medical, financial, legal, or professional advice
• Do not rely on survey content as professional guidance

15. HEALTHCARE SURVEY DISCLAIMER

15.1 NOT MEDICAL ADVICE
• If you participate in healthcare-related surveys:
  - Responses are for research purposes only
  - Do not constitute medical advice or diagnosis
  - Consult a licensed healthcare provider for medical decisions

15.2 HIPAA
• This platform is NOT HIPAA-compliant
• Do not share protected health information (PHI)
• Do not include patient names, medical record numbers, or specific treatment details

16. CHANGES TO TERMS

16.1 UPDATES
• We may update these terms at any time
• Material changes will be posted on the platform with updated date
• Continued participation after updates constitutes acceptance

16.2 NOTIFICATION
• We will attempt to notify active users of significant changes
• Check this page periodically for updates

17. DISPUTE RESOLUTION

Any disputes will be resolved through binding arbitration on an individual basis (not as a class action) under the rules of the American Arbitration Association. You waive your right to sue in court or participate in a class action lawsuit. Small claims court is permitted for qualifying claims. Arbitration will be held in Seattle, Washington.

18. GOVERNING LAW

These terms are governed by the laws of the State of Delaware, United States, without regard to conflict of law provisions. Any arbitration will be held in Seattle, Washington.

19. CHILDREN'S PRIVACY (COPPA COMPLIANCE)

19.1 AGE REQUIREMENT
• You must be at least 18 years old to participate
• If you are under 18, do NOT use this platform

19.2 NO KNOWING COLLECTION FROM MINORS
• We do not knowingly collect data from individuals under 18
• If we discover underage participation, data will be deleted immediately

20. CALIFORNIA PRIVACY RIGHTS (CCPA/CPRA)

20.1 CALIFORNIA RESIDENTS
If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):

- Right to Know: What personal information we collect and how it's used
• Right to Delete: Request deletion of your personal information
• Right to Opt-Out: Opt-out of "sale" or "sharing" of personal information
• Right to Non-Discrimination: We will not discriminate for exercising rights

20.2 CATEGORIES OF PERSONAL INFORMATION COLLECTED
• Identifiers: Customer ID, IP address, external tracking ID
• Internet Activity: Response times, input modes, browser type, referrer
• Audio/Visual: Voice recordings (biometric), uploaded images
• Geolocation: Country/region derived from IP address
• Inferences: Behavioral patterns, preferences, quality scores

20.3 BUSINESS PURPOSES FOR COLLECTION
• ML model training and product development
• Survey administration and quality assurance
• Fraud prevention and security
• Analytics and research insights

20.4 CATEGORIES OF THIRD PARTIES WE SHARE WITH
• AI Service Providers: OpenAI, Anthropic, Google (data processors)
• Cloud Infrastructure: Amazon Web Services (storage)
• Geolocation Services: Third-party IP lookup providers

20.5 NO SALE OF PERSONAL INFORMATION
• We do NOT sell your personal information for monetary consideration
• Sharing with AI processors may constitute "sharing" under CCPA but is necessary for survey functionality

20.6 SENSITIVE PERSONAL INFORMATION (VOICE = BIOMETRIC)
• Voice recordings are considered "sensitive personal information"
• We use voice data ONLY for transcription and ML training
• You have the right to limit use of sensitive PI, but this would prevent participation in voice-enabled surveys

20.7 HOW TO EXERCISE YOUR RIGHTS
• Email: support@blue-pill.ai
• Subject: "CCPA Rights Request"
• Include: Customer ID and specific request
• We will respond within 45 days

21. ACCESSIBILITY

21.1 COMMITMENT
• We strive to make our platform accessible to all users
• Surveys are designed to work with screen readers and assistive technology

21.2 ACCOMMODATIONS
• If you need accommodations to participate, contact us at support@blue-pill.ai
• We strive to make surveys accessible but cannot guarantee all content is fully accessible

22. CONTACT INFORMATION

For questions about these Terms and Conditions:
BluePill, Inc.
501 E Pine St, Suite 201
Seattle, WA 98122
Email: support@blue-pill.ai
Website: https://blue-pill.ai

For all inquiries (privacy, legal, support):
Email: support@blue-pill.ai

23. SEVERABILITY

If any provision of these terms is found unenforceable, the remaining provisions will continue in full force and effect.

24. ENTIRE AGREEMENT

These terms, along with our Privacy Policy, constitute the entire agreement between you and BluePill regarding survey participation.

25. CONSENT CONFIRMATION

By clicking "I Agree" or "Start Survey", you confirm:

✓ I have read and understood these Terms and Conditions
✓ I consent to the collection and use of my survey responses (text, voice, and images) for BluePill's internal machine learning model training
✓ I understand my data will be processed by third-party AI services (OpenAI, Anthropic, Google)
✓ I acknowledge that my data will NOT be sold to third parties
✓ I am at least 18 years of age
✓ I agree to comply with all terms and conditions outlined above
✓ I specifically consent to the collection, storage, and use of my voice recordings (biometric identifiers) for up to 5 years for ML training purposes

END OF TERMS AND CONDITIONS

QUICK SUMMARY:
• We collect your survey responses to train our own AI models
• We do NOT sell your data to other companies
• Third-party AI services (OpenAI, Anthropic, Google) process your data
• Voice recordings are biometric data (special consent required)
• Your data is anonymized but used for training
• You can request deletion, but trained models retain learned patterns
• Must be 18+ to participate
• California residents have additional privacy rights

Thank you for contributing to AI research and product development!
`;

const TERMS_REGION_LABEL = 'Survey terms and data usage consent';
const ACKNOWLEDGEMENT_LABEL = 'I have read and understood the terms above';
const ACCEPT_LABEL = 'I Accept';
const ACCEPT_BLOCKED_LABEL = 'Please scroll or confirm you have read the terms';
const AT_BOTTOM_TOLERANCE_PX = 40;

const SurveyConsentScreen: React.FC<SurveyConsentScreenProps> = ({ onAccept }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  // Scroll position alone cannot gate acceptance: a screen reader reads the terms without ever
  // scrolling the container, which locked those respondents out of every survey (#493).
  const canAccept = hasScrolledToBottom || hasAcknowledged;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < AT_BOTTOM_TOLERANCE_PX;
    if (atBottom) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-blue-50 rounded-full p-3">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 text-center">
            We value your privacy
          </h2>
          <p className="text-sm text-gray-500 text-center mt-2">
            Before you continue, please review our policies.
          </p>
        </div>

        {/* Scrollable Terms Content — tabIndex keeps it scrollable without a mouse */}
        <div
          role="region"
          aria-label={TERMS_REGION_LABEL}
          tabIndex={0}
          className="px-6 py-4 max-h-80 overflow-y-auto text-xs text-gray-600 font-mono whitespace-pre-wrap leading-relaxed border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
          onScroll={handleScroll}
        >
          {TERMS_CONTENT}
        </div>

        {/* Footer */}
        <div className="px-6 py-5">
          <label className="flex items-start gap-3 mb-4 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={hasAcknowledged}
              onChange={(e) => setHasAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
            />
            <span>{ACKNOWLEDGEMENT_LABEL}</span>
          </label>
          <p className="text-sm text-gray-600 text-center mb-4">
            By clicking accept, you acknowledge that you have read and understood the above terms.
          </p>
          <button
            onClick={onAccept}
            disabled={!canAccept}
            className={`w-full px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
              canAccept
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {canAccept ? ACCEPT_LABEL : ACCEPT_BLOCKED_LABEL}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyConsentScreen;
